import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MapPin,
  ArrowRight
} from 'lucide-react';
import finalLogo from '../../assets/logo/final-logo.png';

const FacebookIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
);

const InstagramIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
);

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const getFooterLinkTranslationKey = (name) => {
    switch (name) {
      case 'Home': return 'navbar.home';
      case 'About Us': return 'navbar.about';
      case 'Members': return 'navbar.members';
      case 'News & Updates': return 'footer.newsUpdates';
      case 'Our Branches': return 'navbar.branches';
      case 'Photo Gallery': return 'footer.photoGallery';
      case 'Contact Us': return 'navbar.contact';
      case 'Gold Loan': return 'navbar.goldLoan';
      case 'Mortgage Loan': return 'navbar.mortgageLoan';
      case 'Loan Against Deposit': return 'navbar.loanAgainstDeposit';
      case 'Business Loan': return 'navbar.businessLoan';
      case 'Agriculture Allied Loan': return 'navbar.agricultureLoan';
      case 'Vehicle Loan': return 'navbar.vehicleLoan';
      case 'Fixed Deposit': return 'footer.fixedDeposit';
      case 'Recurring Deposit': return 'footer.recurringDeposit';
      case 'Savings Account': return 'footer.savingsAccount';
      case 'Senior Citizen Scheme': return 'footer.seniorCitizenScheme';
      default: return name;
    }
  };

  const footerLinks = {
    quickLinks: [
      { name: 'Home', path: '/' },
      { name: 'About Us', path: '/about' },
      { name: 'Members', path: '/members' },
      { name: 'News & Updates', path: '/news' },
      { name: 'Our Branches', path: '/branches' },
      { name: 'Photo Gallery', path: '/gallery' },
      { name: 'Contact Us', path: '/contact' },
    ],
    loanServices: [
      { name: 'Gold Loan', path: '/services/gold-loan' },
      { name: 'Mortgage Loan', path: '/services/mortgage-loan' },
      { name: 'Loan Against Deposit', path: '/services/loan-against-deposit' },
      { name: 'Business Loan', path: '/services/business-loan' },
      { name: 'Agriculture Allied Loan', path: '/services/agriculture-allied-loan' },
      { name: 'Vehicle Loan', path: '/services/vehicle-loan' },
    ],
    depositSchemes: [
      { name: 'Fixed Deposit', path: '/deposit-schemes' },
      { name: 'Recurring Deposit', path: '/deposit-schemes' },
      { name: 'Savings Account', path: '/deposit-schemes' },
      { name: 'Senior Citizen Scheme', path: '/deposit-schemes' },
    ]
  };

  return (
    <footer className="bg-primary-dark text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="space-y-6 flex flex-col items-center text-center md:items-start md:text-left">
            <Link to="/" className="inline-block">
              <img
                src={finalLogo}
                alt="Sangram Patsanstha Logo"
                className="w-36 h-36 object-contain rounded-full bg-white p-1 shadow-lg hover:scale-105 transition-transform duration-300"
              />
            </Link>
            <p className="text-gray-400 font-openSans leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="p-2 bg-white/5 hover:bg-primary rounded-full transition-colors duration-300">
                <FacebookIcon className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-white/5 hover:bg-primary rounded-full transition-colors duration-300">
                <InstagramIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <h3 className="text-lg font-bold mb-6 font-montserrat border-l-0 md:border-l-4 border-primary md:pl-3 pl-0 text-center md:text-left">{t('footer.quickLinks')}</h3>
            <ul className="space-y-4 w-full">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-gray-400 hover:text-primary transition-colors duration-300 flex items-center justify-center md:justify-start group">
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {t(getFooterLinkTranslationKey(link.name))}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <h3 className="text-lg font-bold mb-6 font-montserrat border-l-0 md:border-l-4 border-primary md:pl-3 pl-0 text-center md:text-left">{t('footer.ourServices')}</h3>
            <ul className="space-y-4 w-full">
              {footerLinks.loanServices.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-gray-400 hover:text-primary transition-colors duration-300 flex items-center justify-center md:justify-start group">
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {t(getFooterLinkTranslationKey(link.name))}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <h3 className="text-lg font-bold mb-6 font-montserrat border-l-0 md:border-l-4 border-primary md:pl-3 pl-0 text-center md:text-left">{t('footer.contactInfo')}</h3>
            <ul className="space-y-4 w-full">
              <li className="flex flex-col items-center text-center md:flex-row md:items-start md:text-left space-y-2 md:space-y-0 md:space-x-3 text-gray-400">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <span>{t('footer.address')}</span>
              </li>
              <li className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left space-y-2 md:space-y-0 md:space-x-3 text-gray-400">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <span>+91 907 525 1097</span>
              </li>
              <li className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left space-y-2 md:space-y-0 md:space-x-3 text-gray-400">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                <span>sangramgbssppalus@gmail.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 text-center md:text-left">
          <p>© {currentYear} Sangram Patsanstha. {t('footer.allRightsReserved')}</p>
          <p className="mt-4 md:mt-0">
            {t('footer.designDevelopedBy')}{' '}
            <a
              href="https://sahyadritech.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white font-bold hover:text-primary transition-colors"
            >
              Sahyadri Tech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
